import { PromotionListResponseType } from "@sellar-io/api-v2/types";
import { ReactNode, useCallback, useMemo } from "react";
import { usePromotionList } from "../../../../../hooks/api/v2/promotion/usePromotionList";
import { BasketOrderItem, ContextOrderBasketType } from "../../../../../hooks";
import { calculateItemTotal } from "@sellar-io/common";
import { isPromotionalOrderItem } from "./isPromotionalOrderItem";
import dayjs from "dayjs";
import { useSessionStorage } from "usehooks-ts";
import {
  StorefrontPromotionContext,
  PROMOTIONS_PRICE_TIER_ID,
  PROMOTIONS_SUPPLIER_ID,
  findPromotionRewardsWithSpendThresholdGreaterThanOrderSubtotal,
  useClaimedPromotionRewardItemsId,
} from "./StorefrontPromotionProvider.shared";

export type StorefrontPromotionContextApi = {
  isFetchingPromotions: boolean;
  promotions: PromotionListResponseType;
  claimedPromotionRewardItemIds: number[];
  claimPromotionRewardItem: (params: {
    promotionRewardItemIds: number[];
    onComplete: (claimedPromotionRewardItemIds: number[]) => void;
  }) => void;
  unclaimPromotionRewardItem: (params: {
    promotionRewardItemIds: number[];
    onComplete: (claimedPromotionRewardItemIds: number[]) => void;
  }) => void;
  applyEligiblePromotionRewards: ({
    order,
    claimedPromotionRewardItemIds,
  }: {
    order: ContextOrderBasketType;
    claimedPromotionRewardItemIds: number[];
  }) => ContextOrderBasketType;
  setPriceTierId: ({ priceTierId }: { priceTierId: number }) => void;
  setSupplierId: ({ supplierId }: { supplierId: number }) => void;
};

StorefrontPromotionContext.displayName = "StorefrontPromotionContext";

type StorefrontPromotionProviderProps = {
  children: ReactNode;
};

export const StorefrontPromotionProvider = ({
  children,
}: StorefrontPromotionProviderProps) => {
  const [currentPriceTierId, setCurrentPriceTierId] = useSessionStorage<number>(
    PROMOTIONS_PRICE_TIER_ID,
    0,
  );

  const [currentSupplierId, setCurrentSupplierId] = useSessionStorage<number>(
    PROMOTIONS_SUPPLIER_ID,
    0,
  );

  const { claimedPromotionRewardItemIds, setClaimedPromotionRewardItemIds } =
    useClaimedPromotionRewardItemsId();

  const now = dayjs().startOf("day").toISOString();

  // Always send both SupplierId and PriceTierId to the backend.
  // The backend checks the supplier's unbundlingRelease2A flag and picks the right path:
  // - unbundlingRelease2A ON  → supplier-scoped global promotions
  // - unbundlingRelease2A OFF → price-tier-scoped promotions (falls through to PriceTierId)
  const promotionsQuery = usePromotionList(
    {
      query: [
        ...(currentSupplierId
          ? [
              {
                type: "SupplierId" as const,
                modifier: "EQUALS" as const,
                value: currentSupplierId,
              },
            ]
          : []),
        ...(currentPriceTierId
          ? [
              {
                type: "PriceTierId" as const,
                modifier: "EQUALS" as const,
                value: currentPriceTierId,
              },
            ]
          : []),
        {
          type: "isActive" as const,
          modifier: "EQUALS" as const,
          value: true,
        },
        {
          type: "onlyPromotionsWithRewards" as const,
          modifier: "EQUALS" as const,
          value: true,
        },
        {
          type: "validAt" as const,
          modifier: "EQUALS" as const,
          value: now,
        },
      ],
    },
    {
      enabled: Boolean(currentSupplierId) || Boolean(currentPriceTierId),
    },
  );

  const promotions = useMemo(
    () => promotionsQuery.data?.pages[0] ?? [],
    [promotionsQuery.data],
  );

  const isFetchingPromotions = promotionsQuery.isPending;

  const claimPromotionRewardItem: StorefrontPromotionContextApi["claimPromotionRewardItem"] =
    useCallback(
      ({ promotionRewardItemIds, onComplete }) => {
        const updatedClaimedRewardItemIds = Array.from(
          new Set([
            ...claimedPromotionRewardItemIds,
            ...promotionRewardItemIds,
          ]),
        );

        setClaimedPromotionRewardItemIds(updatedClaimedRewardItemIds);

        onComplete(updatedClaimedRewardItemIds);
      },
      [claimedPromotionRewardItemIds, setClaimedPromotionRewardItemIds],
    );

  const unclaimPromotionRewardItem: StorefrontPromotionContextApi["unclaimPromotionRewardItem"] =
    useCallback(
      ({ promotionRewardItemIds, onComplete }) => {
        const updatedClaimedRewardItemIds =
          claimedPromotionRewardItemIds.filter(
            (claimedPromotionRewardItemId) => {
              return !promotionRewardItemIds.includes(
                claimedPromotionRewardItemId,
              );
            },
          );

        setClaimedPromotionRewardItemIds(updatedClaimedRewardItemIds);

        onComplete(updatedClaimedRewardItemIds);
      },
      [claimedPromotionRewardItemIds, setClaimedPromotionRewardItemIds],
    );

  const applyEligiblePromotionRewards: StorefrontPromotionContextApi["applyEligiblePromotionRewards"] =
    useCallback(
      ({ order: basketOrder, claimedPromotionRewardItemIds }) => {
        const nonPromotionBasketOrderItems = (
          basketOrder.OrderItems ?? []
        ).filter((orderItem) => {
          return !isPromotionalOrderItem(orderItem);
        });

        const basketSubtotal = calculateItemTotal(nonPromotionBasketOrderItems);

        const promotionRewardItemIdsToRemove =
          findPromotionRewardsWithSpendThresholdGreaterThanOrderSubtotal({
            basketSubtotal,
            promotions,
            claimedPromotionRewardItemIds,
          });

        const updatedListOfClaimedRewardItemIds =
          claimedPromotionRewardItemIds.filter(
            (claimedPromotionRewardItemId) =>
              !promotionRewardItemIdsToRemove.includes(
                claimedPromotionRewardItemId,
              ),
          );

        setClaimedPromotionRewardItemIds(updatedListOfClaimedRewardItemIds);

        const currentlyClaimedPromotionRewardItems = promotions
          .flatMap((promotion) => promotion.PromotionRewardItems)
          .filter((promotionRewardItem) => {
            return updatedListOfClaimedRewardItemIds.includes(
              promotionRewardItem.id,
            );
          });

        const promotionBasketOrderItems: BasketOrderItem[] =
          currentlyClaimedPromotionRewardItems
            .map((promotionRewardItem) => {
              const matchingProductItem = nonPromotionBasketOrderItems.find(
                (item) => item.Product.id === promotionRewardItem.Product.id,
              );

              if (matchingProductItem) {
                // We freeze the promotional order item because we don't want it to be updated by any
                // side-effects
                return Object.freeze({
                  ...matchingProductItem,
                  quantity: promotionRewardItem.quantity,
                  price: promotionRewardItem.price,
                  PromotionRewardItemId: promotionRewardItem.id,
                  promotionAppliedAt: new Date(),
                });
              }
              // We freeze the promotional order item because we don't want it to be updated by any
              // side-effects
              return Object.freeze({
                id: undefined,
                quantity: promotionRewardItem.quantity,
                price: promotionRewardItem.price,
                fullPrice: promotionRewardItem.price,
                PromotionRewardItemId: promotionRewardItem.id,
                promotionAppliedAt: new Date(),
                ProductId: promotionRewardItem.Product.id,
                discountable: false,
                Product: {
                  ...promotionRewardItem.Product,
                  ProductPriceTier: null,
                },
              });
            })
            .filter(Boolean);

        const newOrderItems = [
          ...nonPromotionBasketOrderItems,
          ...promotionBasketOrderItems,
        ];

        return {
          ...basketOrder,
          OrderItems: newOrderItems,
          RetailerId: basketOrder.RetailerId ?? null,
        };
      },
      [promotions, setClaimedPromotionRewardItemIds],
    );

  /**
   * This determines the promotions available in the provider.
   * It should only be called from {@link useSyncPromotionPriceTier}
   * */
  const setPriceTierId: StorefrontPromotionContextApi["setPriceTierId"] =
    useCallback(
      ({ priceTierId }) => {
        setCurrentPriceTierId(priceTierId);
      },
      [setCurrentPriceTierId],
    );

  /**
   * This determines the supplier-scoped promotions when unbundlingRelease2A is active.
   * It should only be called from {@link useSyncPromotionSupplierId}
   * */
  const setSupplierId: StorefrontPromotionContextApi["setSupplierId"] =
    useCallback(
      ({ supplierId }) => {
        setCurrentSupplierId(supplierId);
      },
      [setCurrentSupplierId],
    );

  const value = useMemo(() => {
    return {
      promotions,
      claimedPromotionRewardItemIds,
      claimPromotionRewardItem,
      unclaimPromotionRewardItem,
      applyEligiblePromotionRewards,
      setPriceTierId,
      setSupplierId,
      isFetchingPromotions,
    };
  }, [
    promotions,
    claimedPromotionRewardItemIds,
    claimPromotionRewardItem,
    unclaimPromotionRewardItem,
    applyEligiblePromotionRewards,
    setPriceTierId,
    setSupplierId,
    isFetchingPromotions,
  ]);

  return (
    <StorefrontPromotionContext.Provider value={value}>
      {children}
    </StorefrontPromotionContext.Provider>
  );
};

/**
 *
 * This syncs the priceTierId from a child component to the
 * StorefrontPromotion provider. This should ideally only be
 * used in one place.
 */

/**
 *
 * This syncs the supplierId from a child component to the
 * StorefrontPromotion provider when unbundlingRelease2A is active.
 * It should ideally only be used in one place.
 */
